import { FC, Fragment } from 'react'
import { Typography, TypographyProps } from '@mui/material'

import { Address } from './Address.js'

type Props = {
  address: Partial<Address>
  separator?: string
  showExtraAddressLine?: boolean
  showExtendedAddress?: boolean
  singleLine?: boolean
}

export const AddressLines: FC<Props> = ({
  address,
  separator = ' ',
  showExtraAddressLine = false,
  showExtendedAddress = false,
  singleLine = false,
}) => {
  if (!address) {
    return null
  }

  const addressComponents = [
    <>
      {address.lines && <span data-test="lines">{address.lines.join(' ')}</span>}
      <span data-test="street">{address.street}</span> <span data-test="houseNumber">{address.houseNumber}</span>
      {address.houseNumberSuffix && (
        <>
          {' '}
          <span data-test="houseNumberSuffix">{address.houseNumberSuffix}</span>
        </>
      )}
    </>,
    showExtraAddressLine && <span data-test="additionalAddressLine">{address.additionalAddressLine}</span>,
    <>
      <span data-test="postalCode">{address.postalCode}</span>{' '}
      {address.suburb && <span data-test="suburb">{address.suburb}</span>} <span data-test="city">{address.city}</span>
    </>,
    showExtendedAddress && <span data-test="country">{address.countryCode}</span>,
  ].filter(Boolean)

  const componentSeparator = singleLine ? separator : <br />

  return (
    <>
      {addressComponents.map((component, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {component}
          {index < addressComponents.length - 1 && componentSeparator}
        </Fragment>
      ))}
    </>
  )
}

export const AddressLine: FC<TypographyProps & Props> = ({
  address,
  separator = ' ',
  showExtraAddressLine = false,
  showExtendedAddress = false,
  singleLine = false,
  variant = 'body1',
  ...props
}) => {
  if (!address) {
    return null
  }

  return (
    <Typography
      {...props}
      variant={variant}
      component="span"
      data-test="addressLine"
      data-testid="addressLine"
      translate="no"
    >
      <AddressLines
        address={address}
        separator={separator}
        showExtraAddressLine={showExtraAddressLine}
        showExtendedAddress={showExtendedAddress}
        singleLine={singleLine}
      />
    </Typography>
  )
}
